import axios from "axios";
import { HEADERS } from "../constants/constant";
import { createTheme } from "@mui/material";
import {
  DATE_FORMAT_1,
  DATE_FORMAT_2,
  DATE_TIME_FORMAT_1,
} from "../constants/constant";

axios.defaults.withCredentials = true;

// utility functions - validation/data functions, API calls, UI utils

// ============================= data validation utils =============================

// validate email
export const validateEmail = (email) => {
  if (email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
  return false;
};

// validate time
export const validateTime = (timeString) => {
  // Check if the input is a string
  if (typeof timeString !== "string") {
    return false;
  }

  // this condition is to return true, if user cleared the input
  if (timeString === "") return true;

  // Regular expression to match the format "HH:MM"
  const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;

  // Test the timeString against the regex pattern
  return timePattern.test(timeString);
};

// Function to convert a file to base64 string
export const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = (error) => {
      reject(error);
    };
  });
};

export const combineString = (str1, str2) => {
  if (str1) return str2 ? `${str1} ${str2}` : str1;
  return str2 ? str2 : "";
};

// possible type =  "MM/dd/yyyy HH:mm" | "yyyy-MM-dd" | "MM/dd/yyyy";
export const formatDate = (inputDateString, type) => {
  if (!inputDateString) return "";
  const dateObject = new Date(inputDateString);

  // Adjust for time zone offset
  const userTimezoneOffset = dateObject.getTimezoneOffset() * 60000;
  const localDateObject = new Date(dateObject.getTime() + userTimezoneOffset);

  if (type === DATE_TIME_FORMAT_1)
    return `${(localDateObject.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${localDateObject
      .getDate()
      .toString()
      .padStart(2, "0")}/${localDateObject.getFullYear()} ${localDateObject
      .getHours()
      .toString()
      .padStart(2, "0")}:${localDateObject
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  else if (type === DATE_FORMAT_1)
    return `${localDateObject.getFullYear()}-${(localDateObject.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${localDateObject
      .getDate()
      .toString()
      .padStart(2, "0")}`;
  else if (type === DATE_FORMAT_2)
    return localDateObject.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
};

export const customDateFilter = (row, columnId, filterValue) => {
  const cellValue = row.getValue(columnId);
  return cellValue?.includes(filterValue) ?? false;
};

// format number to this format (916) 793-6662
export const formatPhoneNumber = (value) => {
  if (!value) return "";
  const numericValue = value.replace(/\D/g, "");

  // Apply the pattern (xxx)-xxx-xxxx
  let formattedNumber = "";
  for (let i = 0; i < numericValue.length; i++) {
    if (i === 0) formattedNumber = `(${numericValue[i]}`;
    else if (i === 3) formattedNumber += `) ${numericValue[i]}`;
    else if (i === 6) formattedNumber += `-${numericValue[i]}`;
    else formattedNumber += numericValue[i];
  }
  return formattedNumber;
};

export const validateData = ({
  value,
  required = false,
  minLength = null,
  maxLength = null,
  min = null,
  max = null,
  email = null,
  phone = null,
  excludeCharacters = null,
}) => {
  // if the value is an empty string and not required then return true
  if (typeof value === "string" && value.trim() === "" && !required) {
    return true;
  }

  let isValid = true;
  if (required) isValid = isValid && value.toString().trim().length !== 0;
  if (minLength !== null && typeof value === "string")
    isValid = isValid && value.trim().length >= minLength;
  if (maxLength !== null && typeof value === "string")
    isValid = isValid && value.trim().length <= maxLength;
  if (min !== null && typeof value === "number")
    isValid = isValid && +value >= min;
  if (max !== null && typeof value === "number")
    isValid = isValid && +value <= max;
  if (email !== null && typeof email === "boolean")
    isValid = isValid && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  if (phone !== null && typeof phone === "boolean")
    isValid = isValid && /^\(\d{3}\) \d{3}-\d{4}$/.test(value);
  if (excludeCharacters && excludeCharacters.length > 0)
    isValid =
      isValid && !excludeCharacters.some((char) => value.includes(char));
  return isValid;
};

// to check empty and "" string
export const isNotEmptyOrNull = (obj, key) => {
  // Check if obj is a string
  if (typeof obj === "string") {
    return obj.trim().length > 0 && obj !== "null";
  }

  // Check if obj is an object and not null
  if (typeof obj !== "object" || obj === null) {
    return false;
  }

  // Check if the key exists in the object
  if (!(key in obj)) {
    return false;
  }

  const value = obj[key];

  // Check if the value is not null and not undefined
  if (value === null || value === undefined) {
    return false;
  }

  // Check if the value is a non-empty string
  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  // For other types, consider them as not empty
  return true;
};

export const replaceNullOrUndefinedValuesToString = (data) => {
  // Iterate over the object keys
  for (let key in data) {
    if (data.hasOwnProperty(key)) {
      // Check if the value is null or undefined, replace with empty string
      if (data[key] === null || data[key] === undefined) {
        data[key] = "";
      }
      // If the value is an object (and not null), recurse into the nested object
      else if (typeof data[key] === "object" && data[key] !== null) {
        replaceNullOrUndefinedValuesToString(data[key]); // Recursion for nested objects
      }
    }
  }
  return data;
};

// ============================= api calls =============================

// Helper function to get cookie by name
export const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
};

// post request - payload is the data to send with the request
export const apiRequest = async ({
  endpoint, // Endpoint to make the request to
  payload, // Data to send with the request
  responseKey = "data",
  defaultValue = [],
  successTitle = "Success", // Success title
  errorTitle = "Error occurred", // Default error title
  requestingHeader = { ...HEADERS },
  config = {}, // Additional axios config
  returnRaw = false, // Return full response
}) => {
  try {
    const csrfToken = getCookie("csrf_access_token");
    if (csrfToken) {
      requestingHeader["X-CSRF-TOKEN"] = csrfToken;
    }

    // If payload is FormData, let the browser set the Content-Type
    if (payload instanceof FormData) {
      delete requestingHeader["Content-Type"];
    }

    const response = await axios.post(
      `${process.env.REACT_APP_API_URI}${endpoint}`,
      payload,
      { headers: requestingHeader, ...config },
    );

    if (returnRaw) return response;

    const { status, message, data = defaultValue } = response.data;
    return {
      status,
      title: status === 1 ? successTitle : errorTitle,
      message: message || "Operation completed successfully",
      [responseKey]: data,
    };
  } catch (error) {
    const typedError = error;

    if (typedError.response && typedError.response.data) {
      // If returnRaw is true, we might want to let the caller handle errors,
      // but keeping consistent behavior for now unless specifically requested otherwise.
      // However, for blob responses, response.data is a Blob, so we can't destructure it easily here
      // without reading it. If returnRaw is true, we probably should re-throw or return a standardized error structure
      // but the caller expects the raw error usually.
      // For this refactor, let's stick to returning the standardized error object
      // BUT if returnRaw is true, the caller might expect the error to be thrown or returned differently.
      // Given the existing usage in AdminServices (printAllCOCs), it catches the error and reads the blob.
      // So if returnRaw is true, we should probably rethrow if it's an error, OR return the error structure.
      // Let's keep it simple: if returnRaw is true, we still catch here but maybe we should rethrow?
      // Actually, looking at LoginServices, it expects null on error.
      // Let's stick to the existing error handling but be mindful of Blob responses.

      if (
        config.responseType === "blob" &&
        typedError.response.data instanceof Blob
      ) {
        throw typedError; // Let the caller handle blob errors (like in printAllCOCs)
      }

      console.error("Server error response:", typedError.response.data);

      const { status, message } = typedError.response.data;

      return {
        status: status,
        title: "Error from server",
        message:
          message ||
          "An error occurred and no message was provided by the server",
      };
    } else {
      const errorMessage = typedError.message || String(typedError);
      return {
        status: 0,
        title: "Exception occurred",
        message: `Exception occurred during the API request: ${errorMessage} for the endpoint: ${endpoint}`,
      };
    }
  }
};

// get request - using the same payload to build query params so that callers can simply send an object
export const apiGETRequest = async ({
  endpoint, // Endpoint to make the request to
  payload, // Object to be converted into query params
  responseKey = "data",
  defaultValue = [],
  successTitle = "Success",
  errorTitle = "Error occurred",
  requestingHeader = { ...HEADERS },
  config = {},
  returnRaw = false,
}) => {
  try {
    let fullUrl = `${process.env.REACT_APP_API_URI}${endpoint}`;

    if (payload && Object.keys(payload).length > 0) {
      const params = new URLSearchParams(payload);
      fullUrl += `?${params.toString()}`;
    }

    const response = await axios.get(fullUrl, {
      headers: requestingHeader,
      ...config,
    });

    if (returnRaw) return response;

    const { status, message, data = defaultValue } = response.data;

    return {
      status,
      title: status === 1 ? successTitle : errorTitle,
      message: message || "Operation completed successfully",
      [responseKey]: data,
    };
  } catch (error) {
    const typedError = error;

    if (typedError.response && typedError.response.data) {
      if (
        config.responseType === "blob" &&
        typedError.response.data instanceof Blob
      ) {
        throw typedError;
      }

      console.error("Server error response:", typedError.response.data);
      const { status, message } = typedError.response.data;
      return {
        status: status,
        title: "Error from server",
        message:
          message ||
          "An error occurred and no message was provided by the server",
      };
    } else {
      const errorMessage = typedError.message || String(typedError);
      return {
        status: 0,
        title: "Exception occurred",
        message: `Exception occurred during the API request: ${errorMessage} for the endpoint: ${endpoint}`,
      };
    }
  }
};

// ============================= UI Utilities =============================

// material table theme
export const materialReactTableThemeDark = createTheme({
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "#ffffff !important",
          },
          "&:hover .MuiSvgIcon-root": {
            color: "#ffffff !important",
          },
          "&.Mui-active .MuiSvgIcon-root": {
            color: "#ffffff !important",
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "#ffffff !important",
          },
          "&:hover .MuiSvgIcon-root": {
            color: "#ffffff !important",
          },
          "&.Mui-active .MuiSvgIcon-root": {
            color: "#ffffff !important",
          },
        },
      },
    },
  },
});

// material table theme
export const materialReactTableThemeLight = createTheme({
  components: {
    MuiIconButton: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "#707070 !important",
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          "& .MuiSvgIcon-root": {
            color: "#707070 !important",
          },
          "&:hover .MuiSvgIcon-root": {
            color: "#ffffff !important",
          },
          "&.Mui-active .MuiSvgIcon-root": {
            color: "#707070 !important",
          },
        },
      },
    },
  },
});
